import { COLORS } from '@btime/constants'
import { concatIf } from '@btime/helpers'
import { navigate } from '@reach/router'
import { useCleanFilterForRequest } from '@btime/react-contexts'
import { useIsOwner, useSessionPermission } from '@btime/react-session'
import React, { useMemo, Fragment } from 'react'

import { Title } from '../../../components/presentation/Text'
import {
  useOpenBucketsModal,
  useOpenEditBucketModal,
  useOpenEditPlaceTypesModal,
  useOpenEditFieldsModal,
  useOpenExportHistoryModal,
  useOpenExportModal,
  useOpenImportHistoryModal,
  useOpenLinktreesModal,
  useOpenPlaceLevelSettingsModal,
  useOpenTemplatesModal,
  useOpenTriggersModal
} from '../../../utils/routes/modals/modalsActions'
import { usePlaceType, useTypePermission, useTypeTranslation } from '../_context/PlaceTypeContext'
import { usePlaceTypeName } from '../../../utils/helpers/placeTypes'
import DropdownLink from '../../../components/navigation/DropdownLink'
import Editable from '../../../packages/kiti/components/form/Editable'
import FlexGrid, { FlexCol } from '../../../components/structure/FlexGrid'
import IconText from '../../../components/presentation/IconText'
import Separator from '../../../components/helpers/Separator'

const PageHeader = () => {
  const placeType = usePlaceType()
  const placeTypeName = usePlaceTypeName(placeType, true)

  const openTriggersModal = useOpenTriggersModal()
  const openImportHistoryModal = useOpenImportHistoryModal()
  const openExportModal = useOpenExportModal()
  const openExportHistoryModal = useOpenExportHistoryModal()
  const openBucketsModal = useOpenBucketsModal()
  const openEditBucketModal = useOpenEditBucketModal()
  const openPlaceLevelSettingsModal = useOpenPlaceLevelSettingsModal()
  const openEditFieldsModal = useOpenEditFieldsModal()
  const openTemplatesModal = useOpenTemplatesModal()
  const openLinktreesModal = useOpenLinktreesModal()
  const openEditPlaceTypesModal = useOpenEditPlaceTypesModal()

  const canEditTriggers = useSessionPermission('editTrigger')
  const canImport = useTypePermission('import')
  const canExport = useTypePermission('export')
  const canSetup = useTypePermission('setup')
  const canListTemplate = useSessionPermission('listTemplate')
  const isOwner = useIsOwner()

  const filter = useCleanFilterForRequest()

  const { t } = useTypeTranslation(['common', 'listPlaces'])

  const options = useMemo(
    () => [
      ...concatIf(canEditTriggers, [
        {
          value: 'triggers',
          iconLeft: 'RiRobotLine',
          onClick: () => openTriggersModal({ entities: [placeType] })
        },

        {
          value: 'integrations',
          iconLeft: 'RiPlugLine',
          onClick: () => openTriggersModal({ entities: [placeType], integrationsOnly: true })
        },

        { line: true }
      ]),

      ...concatIf(canListTemplate, [
        {
          value: 'templates',
          iconLeft: 'RiFileCopy2Line',
          onClick: () => openTemplatesModal(placeType)
        },

        { line: true }
      ]),

      ...concatIf(canImport, [
        {
          value: 'import',
          iconLeft: 'RiUploadCloud2Line',
          onClick: () => navigate(`/import/v2/${placeType}`)
        },
        {
          value: 'importHistory',
          iconLeft: 'RiTimeLine',
          onClick: () => openImportHistoryModal(placeType)
        },

        { line: true }
      ]),

      ...concatIf(canExport, [
        {
          value: 'export',
          iconLeft: 'RiDownloadCloud2Line',
          onClick: () => openExportModal({ entity: placeType, filter: { ...filter, types: [placeType] } })
        },
        {
          value: 'exportHistory',
          iconLeft: 'RiTimeLine',
          onClick: () => openExportHistoryModal(placeType)
        },

        { line: true }
      ]),

      ...concatIf(canSetup, [
        {
          value: 'levelsSettings',
          iconLeft: 'RiNodeTree',
          onClick: () => openPlaceLevelSettingsModal(placeType)
        },
        {
          value: 'fieldsSettings',
          iconLeft: 'RiListSettingsLine',
          onClick: () => openEditFieldsModal(placeType)
        },
        {
          value: 'placeLevelsEdit',
          iconLeft: 'RiListSettingsLine',
          onClick: () => navigate(`/place-levels/${placeType}`)
        },

        { line: true }
      ]),

      ...concatIf(canSetup, [
        {
          value: 'linktree',
          iconLeft: 'RiNodeTree',
          onClick: () => openLinktreesModal({ placeType })
        },

        {
          value: 'qrcode',
          iconLeft: 'RiQrCodeLine',
          onClick: () => openEditBucketModal({ type: placeType })
        },

        {
          value: 'buckets',
          iconLeft: 'RiCheckboxMultipleBlankLine',
          onClick: () => openBucketsModal({ type: placeType })
        }
      ])
    ],
    [
      openEditPlaceTypesModal,
      openEditBucketModal,
      openImportHistoryModal,
      openExportHistoryModal,
      openBucketsModal,
      openTemplatesModal
    ]
  )

  const hideMoreOptions = !options.length

  return (
    <Fragment>
      <Separator weight={12} />
      <FlexGrid justify="space-between" gutter={0.4}>
        <FlexCol>
          <Editable onClick={() => openEditPlaceTypesModal(placeType)} disabled={!isOwner}>
            <Title size="h2" color={COLORS.dark3} noWrap>
              {placeTypeName}
            </Title>
          </Editable>
        </FlexCol>

        {/* TODO: Take it back after migrate intercom articles to another place */}
        {/* <FlexCol>
          <PageGuide />
        </FlexCol> */}

        <FlexCol flex={1}></FlexCol>

        {!hideMoreOptions && (
          <FlexCol>
            <DropdownLink
              fromRight
              t={key => t(`listPlaces:menu.${key}`, key)}
              options={options}
              actionContent={
                <IconText label={t('listPlaces:moreOptions')} iconName="BsThreeDotsVertical" noWrap size="small" bold />
              }
            />
          </FlexCol>
        )}
      </FlexGrid>
      <Separator weight={2} />
    </Fragment>
  )
}

export default PageHeader
